import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { resolveAssetUrl } from '../lib/assets';
import { usePresence } from '../hooks/usePresence';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FriendsSection = () => {
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const [requests, setRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [busy, setBusy] = useState(null);
  const online = usePresence(friends.map((f) => f.id));

  const load = useCallback(async () => {
    try {
      const [rq, fr] = await Promise.all([
        axios.get(`${API}/friends/requests`, { withCredentials: true }),
        axios.get(`${API}/friends`, { withCredentials: true }),
      ]);
      setRequests(rq.data.requests || []);
      setFriends(fr.data.friends || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (userId, action) => {
    setBusy(userId);
    try {
      await axios.post(`${API}/friends/${userId}/${action}`, {}, { withCredentials: true });
      toast.success(action === 'accept' ? tr('Pieņemts!', 'Accepted!', 'Принято!') : tr('Noraidīts', 'Declined', 'Отклонено'));
      load();
    } catch (e) {
      toast.error(tr('Neizdevās', 'Failed', 'Ошибка'));
    } finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="friends-manager">
      <h2 className="font-outfit text-lg font-semibold text-[#121212] mb-1 flex items-center gap-2">
        <Users className="w-5 h-5 text-[#D97736]" /> {tr('Draugi', 'Friends', 'Друзья')}
      </h2>
      <p className="text-sm text-[#5C5C5C] mb-5">
        {tr('Pārvaldi draudzības uzaicinājumus un savus draugus.', 'Manage friend requests and your friends.', 'Управляйте заявками и друзьями.')}
      </p>

      <div className="mb-6">
        <h3 className="text-sm font-medium text-[#121212] mb-3">
          {tr('Uzaicinājumi', 'Requests', 'Заявки')} ({requests.length})
        </h3>
        {requests.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">{tr('Nav jaunu uzaicinājumu', 'No new requests', 'Нет новых заявок')}</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center gap-3" data-testid={`friend-request-${r.id}`}>
                <div className="w-10 h-10 rounded-full bg-[#4A6B53] flex items-center justify-center text-white font-semibold overflow-hidden shrink-0">
                  {r.avatar_url ? <img src={resolveAssetUrl(r.avatar_url)} alt={r.name} className="w-full h-full object-cover" /> : r.name?.charAt(0).toUpperCase()}
                </div>
                <Link to={`/lietotajs/${r.id}`} className="flex-1 min-w-0 font-medium text-[#121212] hover:text-[#D97736] truncate">{r.name}</Link>
                <Button size="sm" onClick={() => respond(r.id, 'accept')} disabled={busy === r.id} className="bg-[#4A6B53] hover:bg-[#3d5945] text-white" data-testid={`accept-request-${r.id}`}>
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => respond(r.id, 'decline')} disabled={busy === r.id} className="border-[#E5E4E0] text-[#5C5C5C]" data-testid={`decline-request-${r.id}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-medium text-[#121212] mb-3">
          {tr('Mani draugi', 'My friends', 'Мои друзья')} ({friends.length})
        </h3>
        {friends.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">{tr('Vēl nav draugu', 'No friends yet', 'Друзей пока нет')}</p>
        ) : (
          <div className="flex flex-wrap gap-4">
            {friends.map((f) => (
              <Link key={f.id} to={`/lietotajs/${f.id}`} className="flex flex-col items-center gap-1 w-16 group" data-testid={`my-friend-${f.id}`}>
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-[#4A6B53] flex items-center justify-center text-white font-semibold overflow-hidden">
                    {f.avatar_url ? <img src={resolveAssetUrl(f.avatar_url)} alt={f.name} className="w-full h-full object-cover" /> : f.name?.charAt(0).toUpperCase()}
                  </div>
                  {online[f.id] && (
                    <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white" title={tr('Tiešsaistē', 'Online', 'Онлайн')} data-testid={`friend-online-${f.id}`} />
                  )}
                </div>
                <span className="text-xs text-[#5C5C5C] truncate w-full text-center group-hover:text-[#D97736]">{f.name}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsSection;
